import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  ShieldCheck,
  Smartphone,
  ClipboardCheck,
  ArrowRight,
  ArrowUpRight,
  Star,
  Quote,
} from "lucide-react";
import CourseSelector from "../components/CourseSelector";
import EndorsementModal from "../components/EndorsementModal";
import { stats, features, steps, testimonials, baseCourses, endorsementCourses } from "../mock";
import { goToCheckout } from "../lib/checkout";

const tickerItems = [
  "100% Online",
  "Listed on the FMCSA Training Provider Registry",
  "$119.95 Per CDL Class",
  "Endorsements $59.95",
  "Practice Test Included",
  "Self-Paced Learning",
];

// Stable keys for the static 5-star ratings (avoid array-index keys)
const STAR_KEYS = ["s1", "s2", "s3", "s4", "s5"];

/* ------------------------------- Ticker ------------------------------- */
function Ticker() {
  const Group = ({ hidden }) => (
    <div className="flex shrink-0 items-center" aria-hidden={hidden}>
      {tickerItems.map((t) => (
        <div key={t} className="flex items-center whitespace-nowrap px-7">
          <span className="w-1.5 h-1.5 rounded-full bg-[#5B8FD1] mr-7" />
          <span className="font-head font-semibold uppercase tracking-widest text-white text-sm leading-none">
            {t}
          </span>
        </div>
      ))}
    </div>
  );
  return (
    <div className="bg-[#16365C] overflow-hidden h-12 flex items-center">
      <div className="flex w-max animate-marquee">
        <Group />
        <Group hidden />
      </div>
    </div>
  );
}

/* -------------------------------- Hero -------------------------------- */
function Hero() {
  return (
    <section className="relative min-h-[640px] lg:min-h-[90vh] flex items-center overflow-hidden">
      {/* Background image */}
      <div className="absolute inset-0">
        {/* Desktop / tablet wide banner */}
        <img
          src="/hero-bg.webp"
          alt="Semi truck on a highway heading toward a city at sunset"
          className="hidden lg:block w-full h-full object-cover object-center"
        />
        {/* Mobile portrait */}
        <img
          src="/hero-bg-mobile.webp"
          alt="Semi truck on a highway heading toward a city at sunset"
          className="lg:hidden w-full h-full object-cover object-center"
        />
        {/* Desktop: left-to-right navy wash for text legibility */}
        <div className="hidden lg:block absolute inset-0 bg-gradient-to-r from-[#08172a] via-[#0B1E33]/85 to-[#0B1E33]/30" />
        {/* Mobile: top-to-bottom wash (image already dark at top) */}
        <div className="lg:hidden absolute inset-0 bg-gradient-to-b from-[#08172a]/75 via-[#0B1E33]/45 to-[#0B1E33]/60" />
        {/* Bottom fade into the navy ticker below */}
        <div className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-b from-transparent to-[#16365C]" />
      </div>

      <div className="relative w-full max-w-7xl mx-auto px-5 lg:px-8 pt-28 pb-20 lg:py-24">
        <div className="max-w-2xl animate-fade-up">
          <div className="inline-flex items-center gap-2 border border-white/25 bg-white/10 backdrop-blur-sm rounded-full px-4 py-1.5 mb-7">
            <ShieldCheck className="w-4 h-4 text-[#8FC0F0]" />
            <span className="font-head font-semibold text-xs tracking-widest uppercase text-white">
              100% Online • Listed on the FMCSA Training Provider Registry
            </span>
          </div>

          <h1 className="font-display text-[3.4rem] sm:text-6xl lg:text-[5rem] leading-[0.95] text-white">
            Drivers Build
            <br />
            <span className="text-[#8FC0F0]">Brighter Futures</span>
          </h1>

          <p className="font-head font-semibold text-xl sm:text-2xl text-white/95 mt-4 tracking-wide">
            ELDT Theory Training — 100% Online
          </p>

          <p className="text-lg text-white/85 mt-6 max-w-xl leading-relaxed">
            100% online, self-paced Entry-Level Driver Training designed by CDL
            instructors with 100+ years of experience — no classroom required.
            CDL classes just{" "}
            <span className="text-white font-semibold">$119.95</span>,
            endorsements only{" "}
            <span className="text-white font-semibold">$59.95</span>.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 mt-9">
            <a
              href="#select-courses"
              className="bg-white text-brand font-head font-bold uppercase tracking-wide rounded-md px-8 py-4 text-base hover:bg-slate-100 transition-colors inline-flex items-center justify-center gap-2"
            >
              Select Courses <ArrowRight className="w-5 h-5" />
            </a>
            <Link
              to="/why-eldt"
              className="border border-white/70 text-white font-head font-bold uppercase tracking-wide rounded-md px-8 py-4 text-base hover:bg-white/10 transition-colors inline-flex items-center justify-center"
            >
              Why ELDT?
            </Link>
          </div>

          <div className="flex flex-wrap gap-x-6 gap-y-3 mt-10">
            {[
              { icon: Smartphone, label: "100% Online" },
              { icon: ShieldCheck, label: "On FMCSA Registry" },
              { icon: ClipboardCheck, label: "Practice Test Included" },
            ].map((b) => (
              <div key={b.label} className="flex items-center gap-2 text-white/85">
                <b.icon className="w-5 h-5 text-[#8FC0F0]" />
                <span className="font-head font-semibold text-sm uppercase tracking-wide">
                  {b.label}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ------------------------------- Stats -------------------------------- */
function StatsBand() {
  return (
    <section className="bg-[#16365C] relative overflow-hidden">
      <div className="absolute inset-0 diag-lines opacity-30" style={{ filter: "invert(1)" }} />
      <div className="relative max-w-7xl mx-auto px-5 lg:px-8 py-12">
        <div className="grid grid-cols-2 lg:grid-cols-4 divide-y lg:divide-y-0 lg:divide-x divide-white/15">
          {stats.map((s) => (
            <div key={s.label} className="px-2 lg:px-8 py-4 lg:py-0">
              <div className="font-display text-4xl lg:text-5xl text-white">
                {s.value}
              </div>
              <div className="font-head font-medium text-xs uppercase tracking-widest text-[#9FBBDD] mt-2">
                {s.label}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------------------------- Bento features -------------------------- */
function FeaturesBento() {
  const [big, ...rest] = features; // first = highlighted tile
  return (
    <section className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
          <div className="max-w-xl">
            <span className="eyebrow">Unprecedented Service &amp; Value</span>
            <h2 className="font-display text-4xl md:text-5xl text-brand mt-3">
              A Better Way To Get Trained
            </h2>
          </div>
          <p className="text-slate-500 max-w-sm">
            Delivered 100% online and self-paced — our courses include every
            topic mandated by FMCSA regulations. Nothing skipped, nothing upsold.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-5">
          {/* Highlighted navy tile */}
          <div className="md:col-span-2 md:row-span-1 bg-[#16365C] rounded-2xl p-9 relative overflow-hidden card-lift">
            <div className="absolute inset-0 diag-lines opacity-30" style={{ filter: "invert(1)" }} />
            <div className="relative">
              <div className="w-14 h-14 rounded-xl bg-white/10 flex items-center justify-center mb-6">
                <big.icon className="w-7 h-7 text-white" />
              </div>
              <h3 className="font-display text-3xl text-white mb-3">
                {big.title}
              </h3>
              <p className="text-[#B9CBE2] leading-relaxed max-w-md">{big.text}</p>
            </div>
          </div>

          {/* smaller tiles */}
          {rest.map((f) => (
            <div
              key={f.title}
              className="card-lift bg-white border border-slate-200 rounded-2xl p-7"
            >
              <div className="w-12 h-12 rounded-lg bg-[#16365C]/8 flex items-center justify-center mb-5">
                <f.icon className="w-6 h-6 text-steel" />
              </div>
              <h3 className="font-head font-semibold text-slate-900 text-lg mb-2">
                {f.title}
              </h3>
              <p className="text-slate-500 text-sm leading-relaxed">{f.text}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* --------------------- Programs: editorial list ----------------------- */
function Programs() {
  const [modalOpen, setModalOpen] = useState(false);
  const [activeCourse, setActiveCourse] = useState(null);
  const openModal = (id) => {
    setActiveCourse(id);
    setModalOpen(true);
  };

  return (
    <section className="py-24 bg-[#F4F7FB]">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
          <div className="max-w-xl">
            <span className="eyebrow">Our Programs</span>
            <h2 className="font-display text-4xl md:text-5xl text-brand mt-3">
              Choose Your Class
            </h2>
          </div>
          <Link
            to="/courses"
            className="font-head font-semibold uppercase tracking-wide text-steel hover:text-brand transition-colors inline-flex items-center gap-2"
          >
            View all courses <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {baseCourses.map((c) => (
            <button
              key={c.id}
              onClick={() => openModal(c.id)}
              className="group text-left bg-white border border-slate-200 rounded-2xl overflow-hidden transition-all duration-300 hover:border-brand hover:shadow-[0_24px_44px_-24px_rgba(22,54,92,0.35)]"
            >
              <div className="relative bg-white p-4">
                <img
                  src={c.image}
                  alt={c.name}
                  className="w-full h-36 object-contain transition-transform duration-500 group-hover:scale-105"
                />
                {c.popular && (
                  <span className="absolute top-3 left-3 bg-brand text-white font-head font-semibold text-[10px] uppercase tracking-wider px-2.5 py-1 rounded-full">
                    Most Popular
                  </span>
                )}
              </div>
              <div className="p-5 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <h3 className="font-head font-semibold text-slate-900 text-lg">
                    {c.name}
                  </h3>
                  <p className="text-slate-400 text-xs mt-0.5">{c.tagline}</p>
                </div>
                <div className="shrink-0 flex items-center gap-3">
                  <span className="font-display text-2xl text-brand">
                    ${c.price}
                  </span>
                  <span className="w-10 h-10 rounded-full bg-[#16365C]/8 group-hover:bg-brand flex items-center justify-center transition-colors">
                    <ArrowUpRight className="w-5 h-5 text-brand group-hover:text-white transition-colors" />
                  </span>
                </div>
              </div>
            </button>
          ))}
        </div>

        {/* Endorsements */}
        <div className="mt-16">
          <div className="max-w-xl mb-8">
            <span className="eyebrow">Add An Endorsement</span>
            <h3 className="font-display text-3xl md:text-4xl text-brand mt-2">
              Endorsement Courses
            </h3>
            <p className="text-slate-500 mt-2">
              Buy any endorsement on its own or add it to a class — each is just
              ${endorsementCourses[0].price}.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {endorsementCourses.map((c) => (
              <button
                key={c.id}
                onClick={() => goToCheckout([c.id])}
                className="group text-left bg-white border border-slate-200 rounded-2xl overflow-hidden transition-all duration-300 hover:border-brand hover:shadow-[0_24px_44px_-24px_rgba(22,54,92,0.35)]"
              >
                <div className="bg-white p-4">
                  <img
                    src={c.image}
                    alt={c.name}
                    className="w-full h-36 object-contain transition-transform duration-500 group-hover:scale-105"
                  />
                </div>
                <div className="p-5 flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <h4 className="font-head font-semibold text-slate-900 text-lg">
                      {c.name}
                    </h4>
                    <p className="text-slate-400 text-xs mt-0.5">{c.tagline}</p>
                  </div>
                  <div className="shrink-0 flex items-center gap-3">
                    <span className="font-display text-2xl text-brand">
                      ${c.price}
                    </span>
                    <span className="w-10 h-10 rounded-full bg-[#16365C]/8 group-hover:bg-brand flex items-center justify-center transition-colors">
                      <ArrowUpRight className="w-5 h-5 text-brand group-hover:text-white transition-colors" />
                    </span>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>

      <EndorsementModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        baseCourseId={activeCourse}
      />
    </section>
  );
}

/* --------------------------- Steps timeline --------------------------- */
function Timeline() {
  return (
    <section className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <div className="text-center max-w-2xl mx-auto mb-16">
          <span className="eyebrow">How It Works</span>
          <h2 className="font-display text-4xl md:text-5xl text-brand mt-3">
            From Sign-Up To The Road
          </h2>
        </div>

        <div className="relative grid md:grid-cols-4 gap-8">
          {/* connecting line */}
          <div className="hidden md:block absolute top-7 left-[12.5%] right-[12.5%] h-0.5 bg-slate-200" />
          {steps.map((s) => (
            <div key={s.number} className="relative text-center md:text-left">
              <div className="mx-auto md:mx-0 w-14 h-14 rounded-full bg-brand text-white font-display text-xl flex items-center justify-center relative z-10 border-4 border-white">
                {s.number}
              </div>
              <h3 className="font-head font-semibold text-slate-900 text-lg mt-5 mb-2">
                {s.title}
              </h3>
              <p className="text-slate-500 text-sm leading-relaxed">{s.text}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* --------------------------- Testimonials ----------------------------- */
function Testimonials() {
  const [featured, ...others] = testimonials;
  return (
    <section className="py-24 bg-[#F4F7FB]">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <span className="eyebrow">Driver Stories</span>
          <h2 className="font-display text-4xl md:text-5xl text-brand mt-3">
            Trusted By Drivers
          </h2>
        </div>

        <div className="grid lg:grid-cols-5 gap-6">
          {/* Featured */}
          <div className="lg:col-span-3 bg-[#16365C] rounded-2xl p-9 relative overflow-hidden">
            <div className="absolute inset-0 diag-lines opacity-30" style={{ filter: "invert(1)" }} />
            <div className="relative">
              <Quote className="w-10 h-10 text-[#5B8FD1] mb-5" />
              <div className="flex gap-1 mb-5">
                {STAR_KEYS.map((k) => (
                  <Star key={k} className="w-5 h-5 fill-[#F0A500] text-[#F0A500]" />
                ))}
              </div>
              <p className="text-white text-2xl font-head leading-snug mb-7">
                “{featured.quote}”
              </p>
              <div>
                <div className="font-head font-semibold text-white text-lg">
                  {featured.name}
                </div>
                <div className="text-[#9FBBDD] text-sm uppercase tracking-wide">
                  {featured.role}
                </div>
              </div>
            </div>
          </div>

          {/* Others stacked */}
          <div className="lg:col-span-2 space-y-6">
            {others.map((t) => (
              <div
                key={t.name}
                className="bg-white border border-slate-200 rounded-2xl p-7 soft-shadow"
              >
                <div className="flex gap-1 mb-3">
                  {STAR_KEYS.map((k) => (
                    <Star key={k} className="w-4 h-4 fill-[#F0A500] text-[#F0A500]" />
                  ))}
                </div>
                <p className="text-slate-600 leading-relaxed mb-4">“{t.quote}”</p>
                <div className="font-head font-semibold text-slate-900">
                  {t.name}
                </div>
                <div className="text-steel text-xs uppercase tracking-wide font-semibold">
                  {t.role}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ------------------------------ Final CTA ----------------------------- */
function FinalCTA() {
  return (
    <section className="relative py-24 overflow-hidden bg-[#16365C]">
      <div className="absolute inset-0 diag-lines opacity-30" style={{ filter: "invert(1)" }} />
      <div className="relative max-w-4xl mx-auto px-5 lg:px-8 text-center">
        <h2 className="font-display text-4xl md:text-6xl text-white leading-tight">
          Ready To Accelerate
          <br /> Your Career?
        </h2>
        <p className="text-white/80 text-lg mt-5 max-w-2xl mx-auto">
          Start your training today — 100% online and completely self-paced.
          Study anywhere, on any device, on your own schedule.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center mt-9">
          <a
            href="#select-courses"
            className="bg-white text-brand font-head font-bold uppercase tracking-wide rounded-md px-8 py-4 hover:bg-slate-100 transition-colors inline-flex items-center justify-center gap-2"
          >
            Get Started <ArrowRight className="w-5 h-5" />
          </a>
          <Link
            to="/contact"
            className="border border-white/60 text-white font-head font-bold uppercase tracking-wide rounded-md px-8 py-4 hover:bg-white/10 transition-colors"
          >
            Talk To Us
          </Link>
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <main>
      <Hero />
      <Ticker />
      <FeaturesBento />
      <Programs />
      <Timeline />
      <StatsBand />
      <CourseSelector />
      <Testimonials />
      <FinalCTA />
    </main>
  );
}
